import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchCommunityGradingTree } from '../../../services/gradingService';
import { normalizeGradingTreePayload } from '../admin/grading/utils';
import type { GradingTreePayload } from '../admin/grading/types';
import { listLocalEmployees, type LocalEmployee } from '../../../services/localEmployeesStore';
import {
  deleteEmployeeGradingOverride,
  deletePositionGrading,
  getEmployeeGradingOverride,
  getPositionGrading,
  listEmployeeGradingOverrides,
  listPositionGradings,
  saveEmployeeGradingOverride,
  savePositionGrading,
  type EmployeeGradingOverride,
  type MatrixSelections,
  type PositionGrading,
} from '../../../services/localEmployeeGradingStore';
import GradingMatrixTable from './GradingMatrixTable';
import {
  buildGradingMatrix,
  formatGradingResult,
  isMatrixComplete,
  migrateSelectionsIfNeeded,
  resolveGradingFromSelections,
} from './employeeGradingEngine';
import './EmployeeGradingSection.css';

type GradingTarget =
  | { kind: 'position'; position: string }
  | { kind: 'employee'; employee: LocalEmployee };

export type GradingSavedHighlight =
  | { kind: 'position'; positionKey: string }
  | { kind: 'employee'; employeeId: string };

function uniquePositions(employees: LocalEmployee[]): string[] {
  const set = new Set<string>();
  employees.forEach((e) => {
    const p = e.position.trim();
    if (p) set.add(p);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

type EmployeeGradingSectionProps = {
  gradeEmployeeIntent?: LocalEmployee | null;
  gradePositionIntent?: string | null;
  onGradeIntentHandled?: () => void;
  onGradingSaved?: (highlight: GradingSavedHighlight) => void;
};

export default function EmployeeGradingSection({
  gradeEmployeeIntent,
  gradePositionIntent,
  onGradeIntentHandled,
  onGradingSaved,
}: EmployeeGradingSectionProps) {
  const [employees, setEmployees] = useState<LocalEmployee[]>([]);
  const [positionGradings, setPositionGradings] = useState<PositionGrading[]>([]);
  const [employeeOverrides, setEmployeeOverrides] = useState<EmployeeGradingOverride[]>([]);
  const [tree, setTree] = useState<GradingTreePayload | null>(null);

  const [loading, setLoading] = useState(true);
  const [treeLoading, setTreeLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  const [target, setTarget] = useState<GradingTarget | null>(null);
  const [familyId, setFamilyId] = useState<number | null>(null);
  const [selections, setSelections] = useState<MatrixSelections>({});
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [emps, posGrades, empGrades] = await Promise.all([
        listLocalEmployees(),
        listPositionGradings(),
        listEmployeeGradingOverrides(),
      ]);
      setEmployees(emps);
      setPositionGradings(posGrades);
      setEmployeeOverrides(empGrades);
    } catch (e) {
      console.error(e);
      setError('Не удалось загрузить локальные данные.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTreeLoading(true);
      try {
        const data = await fetchCommunityGradingTree();
        if (!cancelled) setTree(normalizeGradingTreePayload(data));
      } catch (e) {
        console.error(e);
        if (!cancelled) setError('Не удалось загрузить дерево грейдинга.');
      } finally {
        if (!cancelled) setTreeLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const positions = useMemo(() => uniquePositions(employees), [employees]);

  const overrideByEmployeeId = useMemo(() => {
    const map = new Map<string, EmployeeGradingOverride>();
    employeeOverrides.forEach((o) => map.set(o.employeeId, o));
    return map;
  }, [employeeOverrides]);

  const gradingByPosition = useMemo(() => {
    const map = new Map<string, PositionGrading>();
    positionGradings.forEach((g) => map.set(g.position.trim().toLowerCase(), g));
    return map;
  }, [positionGradings]);

  const getEffectiveGrading = useCallback((emp: LocalEmployee) => {
    const override = overrideByEmployeeId.get(emp.id);
    if (override) return { source: 'employee' as const, grading: override };
    const pos = gradingByPosition.get(emp.position.trim().toLowerCase());
    if (pos) return { source: 'position' as const, grading: pos };
    return null;
  }, [gradingByPosition, overrideByEmployeeId]);

  const families = tree?.families ?? [];
  const matrix = familyId && tree ? buildGradingMatrix(familyId, tree) : null;

  const resolvedRole = matrix && tree
    ? resolveGradingFromSelections(selections, tree)
    : null;

  const matrixComplete = matrix && tree
    ? isMatrixComplete(selections, matrix, tree)
    : false;

  useEffect(() => {
    if (!matrix || !tree) return;
    setSelections((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      return migrateSelectionsIfNeeded(prev, matrix, tree);
    });
  }, [matrix, tree]);

  const resetWizard = () => {
    setTarget(null);
    setFamilyId(null);
    setSelections({});
    setSaveMessage('');
  };

  const startPositionGrading = async (position: string) => {
    setTarget({ kind: 'position', position });
    setSaveMessage('');
    const existing = await getPositionGrading(position);
    if (existing) {
      setFamilyId(existing.familyId);
      setSelections(existing.selections);
    } else {
      setFamilyId(null);
      setSelections({});
    }
  };

  const startEmployeeGrading = async (employee: LocalEmployee) => {
    setTarget({ kind: 'employee', employee });
    setSaveMessage('');
    const override = await getEmployeeGradingOverride(employee.id);
    if (override) {
      setFamilyId(override.familyId);
      setSelections(override.selections);
      return;
    }
    const posGrade = await getPositionGrading(employee.position);
    if (posGrade) {
      setFamilyId(posGrade.familyId);
      setSelections(posGrade.selections);
    } else {
      setFamilyId(null);
      setSelections({});
    }
  };

  useEffect(() => {
    if (!gradeEmployeeIntent || loading) return;
    void startEmployeeGrading(gradeEmployeeIntent);
    onGradeIntentHandled?.();
  }, [gradeEmployeeIntent, loading, onGradeIntentHandled]);

  useEffect(() => {
    if (!gradePositionIntent || loading) return;
    void startPositionGrading(gradePositionIntent);
    onGradeIntentHandled?.();
  }, [gradePositionIntent, loading, onGradeIntentHandled]);

  const onSelectCell = (rowKey: string, answerId: number) => {
    setSelections((prev) => ({ ...prev, [rowKey]: answerId }));
  };

  const onSave = async () => {
    if (!target || !tree || !familyId || !matrix || !resolvedRole || !matrixComplete) return;
    setSaving(true);
    setError('');
    setSaveMessage('');
    try {
      const family = tree.families.find((f) => f.id === familyId);
      const roleCluster = tree.roleClusters.find((rc) => rc.id === resolvedRole.role_cluster_id);
      if (!family || !roleCluster) throw new Error('Не найдено семейство или role cluster');

      const roleClusterId = resolvedRole.role_cluster_id;
      const now = new Date().toISOString();
      if (target.kind === 'position') {
        const positionKey = target.position.trim().toLowerCase();
        await savePositionGrading({
          position: target.position,
          familyId,
          familyName: family.name,
          roleClusterId,
          roleClusterName: roleCluster.name,
          selections,
          roleId: resolvedRole.id,
          roleName: resolvedRole.name,
          gradeLevel: resolvedRole.grade_level,
          updatedAt: now,
        });
        onGradingSaved?.({
          kind: 'position',
          positionKey,
        });
      } else {
        const employeeId = target.employee.id;
        await saveEmployeeGradingOverride({
          employeeId,
          familyId,
          familyName: family.name,
          roleClusterId,
          roleClusterName: roleCluster.name,
          selections,
          roleId: resolvedRole.id,
          roleName: resolvedRole.name,
          gradeLevel: resolvedRole.grade_level,
          updatedAt: now,
        });
        onGradingSaved?.({
          kind: 'employee',
          employeeId,
        });
      }
      await reload();
      resetWizard();
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'Не удалось сохранить грейд.');
    } finally {
      setSaving(false);
    }
  };

  const onClearPositionGrading = async (position: string) => {
    if (!window.confirm(`Сбросить грейд для должности «${position}»?`)) return;
    await deletePositionGrading(position);
    if (target?.kind === 'position' && target.position === position) resetWizard();
    await reload();
  };

  const onClearEmployeeOverride = async (employeeId: string) => {
    if (!window.confirm('Сбросить индивидуальный грейд и вернуть грейд должности?')) return;
    await deleteEmployeeGradingOverride(employeeId);
    if (target?.kind === 'employee' && target.employee.id === employeeId) resetWizard();
    await reload();
  };

  const wizardTitle = target?.kind === 'position'
    ? `Грейдирование должности: ${target.position}`
    : target?.kind === 'employee'
      ? `Перегрейдирование: ${target.employee.fullName}`
      : '';

  return (
    <div className="emp-grading">
      <div className="emp-grading-header">
        <div>
          <h2 className="emp-grading-title">
            <i className="fas fa-layer-group" aria-hidden="true" />
            Грейдирование
          </h2>
          <p className="emp-grading-subtitle">
            Сначала грейдируйте должность — все сотрудники с этой должностью получат тот же грейд. При необходимости перегрейдируйте отдельного сотрудника.
          </p>
        </div>
      </div>

      {error && <div className="emp-grading-alert emp-grading-alert-error" role="alert">{error}</div>}
      {saveMessage && <div className="emp-grading-alert emp-grading-alert-success" role="status">{saveMessage}</div>}

      {treeLoading && (
        <div className="my-employees-card">Загрузка дерева грейдинга…</div>
      )}

      {!target ? (
        <>
          <div className="my-employees-card emp-grading-positions-panel">
            <h3 className="emp-grading-block-title">Должности</h3>
            {loading ? (
              <p className="emp-grading-muted">Загрузка…</p>
            ) : positions.length === 0 ? (
              <p className="emp-grading-muted">
                Сначала добавьте сотрудников на вкладке «Мои сотрудники» — должности появятся из их позиций.
              </p>
            ) : (
              <div className="emp-grading-positions">
                {positions.map((position) => {
                  const grading = gradingByPosition.get(position.trim().toLowerCase());
                  const count = employees.filter((e) => e.position === position).length;
                  return (
                    <div key={position} className="emp-grading-position-card">
                      <div className="emp-grading-position-info">
                        <div className="emp-grading-position-name">{position}</div>
                        <div className="emp-grading-muted">{count} сотр.</div>
                        {grading ? (
                          <div className="emp-grading-position-result">
                            {formatGradingResult({ name: grading.roleName })}
                          </div>
                        ) : (
                          <div className="emp-grading-muted">Грейд не назначен</div>
                        )}
                      </div>
                      <div className="emp-grading-position-actions">
                        <button type="button" className="my-employees-btn my-employees-btn-primary" onClick={() => startPositionGrading(position)}>
                          {grading ? 'Изменить грейд' : 'Грейдировать'}
                        </button>
                        {grading && (
                          <button type="button" className="my-employees-btn my-employees-btn-secondary" onClick={() => onClearPositionGrading(position)}>
                            Сбросить
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="my-employees-card">
            <h3 className="emp-grading-block-title">Сотрудники</h3>
            {loading ? (
              <p className="emp-grading-muted">Загрузка…</p>
            ) : employees.length === 0 ? (
              <p className="emp-grading-muted">Нет сотрудников.</p>
            ) : (
              <div className="my-employees-table-wrap">
                <table className="my-employees-table">
                  <thead>
                    <tr>
                      <th>Имя</th>
                      <th>Должность</th>
                      <th>Грейд</th>
                      <th>Источник</th>
                      <th style={{ textAlign: 'right' }}>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp) => {
                      const effective = getEffectiveGrading(emp);
                      return (
                        <tr key={emp.id}>
                          <td>{emp.fullName}</td>
                          <td>{emp.position}</td>
                          <td>
                            {effective
                              ? formatGradingResult({ name: effective.grading.roleName })
                              : '—'}
                          </td>
                          <td>
                            {effective?.source === 'employee' ? 'Индивидуальный' : effective ? 'Должность' : '—'}
                          </td>
                          <td>
                            <div className="my-employees-row-actions">
                              <button type="button" className="my-employees-link" onClick={() => startEmployeeGrading(emp)}>
                                Перегрейдировать
                              </button>
                              {overrideByEmployeeId.has(emp.id) && (
                                <button type="button" className="my-employees-link" onClick={() => onClearEmployeeOverride(emp.id)}>
                                  Сбросить
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="my-employees-card emp-grading-wizard">
          <div className="emp-grading-wizard-head">
            <h3 className="emp-grading-block-title">{wizardTitle}</h3>
            <button type="button" className="my-employees-link" onClick={resetWizard}>← Назад к списку</button>
          </div>

          <div className="emp-grading-steps">
            <div className="emp-grading-step">
              <label className="emp-grading-step-label">1. Семейство (Family)</label>
              <select
                className="emp-grading-select"
                value={familyId ?? ''}
                onChange={(e) => {
                  const next = e.target.value ? Number(e.target.value) : null;
                  setFamilyId(next);
                  setSelections({});
                }}
              >
                <option value="">Выберите семейство</option>
                {families.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          </div>

          {familyId != null && matrix && (
            <>
              <div className="emp-grading-step">
                <label className="emp-grading-step-label">
                  2. Матрица грейдинга — выберите ответ в каждой строке
                </label>
                <p className="emp-grading-muted">
                  Выберите подходящий ответ в каждой строке для вашего кластера ролей. Строки, где для кластера нет вариантов (—), можно пропустить.
                </p>
              </div>

              <GradingMatrixTable
                rows={matrix.rows}
                columns={matrix.columns}
                cells={matrix.cells}
                selections={selections}
                onSelect={onSelectCell}
              />

              <div className="emp-grading-result-bar">
                <div>
                  <span className="emp-grading-muted">Результат: </span>
                  <strong>
                    {resolvedRole && matrixComplete
                      ? formatGradingResult(resolvedRole)
                      : matrixComplete ? '—' : 'Выберите ответ в каждой строке'}
                  </strong>
                </div>
                <div className="emp-grading-wizard-actions">
                  <button
                    type="button"
                    className="my-employees-btn my-employees-btn-primary"
                    disabled={!matrixComplete || !resolvedRole || saving}
                    onClick={onSave}
                  >
                    {saving ? 'Сохранение…' : 'Сохранить грейд'}
                  </button>
                  {target.kind === 'employee' && overrideByEmployeeId.has(target.employee.id) && (
                    <button
                      type="button"
                      className="my-employees-btn my-employees-btn-secondary"
                      onClick={() => onClearEmployeeOverride(target.employee.id)}
                    >
                      Сбросить индивидуальный грейд
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
